import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { DateTime } from "luxon"
import { useFetchLeadScoresQuery } from "../../../../redux/queries"
import { useUserRoles } from "../../../hooks"
import { getUserBrandId } from "../../../utils"
import { LeadScore, LeadScoreCategory } from "../../../../types"
import If from "../../if"
import PillTabs, { TabProps } from "../../pill-tabs"
import Select from "../../form/select"
import {
  CATEGORY_CONFIG,
  CATEGORIES
} from "../../lead-scoring/category-helpers"
import { CategoryChip, ScoreBadge } from "../../lead-scoring/score-row-shared"
import ActivitySkeleton from "../../skeletons/dashboard/activity"
import aiIcon from "../../../../assets/icons/ai.svg"

const MAX_PREVIEW_ITEMS = 5

const CATEGORY_TABS: TabProps[] = CATEGORIES.map(c => ({
  id: c,
  label: CATEGORY_CONFIG[c].labelPlural
}))

const CATEGORY_SELECT_OPTIONS = CATEGORIES.map(c => ({
  id: c,
  name: CATEGORY_CONFIG[c].labelPlural
}))

const LeadScorePreviewCard = ({ score }: { score: LeadScore }) => {
  const sub = score.subscription
  const displayName =
    sub?.first_name && sub?.last_name
      ? `${sub.first_name} ${sub.last_name}`
      : sub?.email ?? ""

  const scoredAtTimestamp = Number(score.scored_at)
  const scoredAtRelative =
    scoredAtTimestamp && !isNaN(scoredAtTimestamp)
      ? DateTime.fromSeconds(scoredAtTimestamp).toRelative() ?? ""
      : ""

  return (
    <Link
      to={`/engagements/${score.subscription_id}`}
      className="flex items-start gap-3 py-3 px-2 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer group"
    >
      <ScoreBadge score={Number(score.intent_score)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base font-bold truncate">{displayName}</span>
          <CategoryChip category={score.category} />
          <span className="ml-auto shrink-0 text-sm text-zinc-400 whitespace-nowrap">
            {scoredAtRelative}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <img src={aiIcon} alt="" className="w-7 h-7 shrink-0 mt-px -ml-1" />
          <p className="text-xs leading-5 line-clamp-2">{score.summary}</p>
        </div>
      </div>
    </Link>
  )
}

const DashboardLeadScoringPreview = () => {
  const [activeTab, setActiveTab] = useState<LeadScoreCategory>("hot_lead")

  const containerRef = useRef<HTMLDivElement>(null)
  // Default true so initial render shows tabs (avoids flash on wide layouts)
  const [showTabs, setShowTabs] = useState(true)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setShowTabs(entry.contentRect.width >= 512)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const { user, isSuperAdmin, isBankAdmin, isLoanOfficer } = useUserRoles()

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean | undefined> = {
      limit: MAX_PREVIEW_ITEMS,
      min_score: 50,
      category: activeTab
    }
    if (!isSuperAdmin && !isBankAdmin && isLoanOfficer && user?.id) {
      params.loid = user.id
    } else if (!isSuperAdmin && isBankAdmin && user?.id && user?.org) {
      const brandId =
        getUserBrandId(user.org, user.id) ??
        user.org?.brands?.[0]?.id ??
        user?.brand_id
      if (brandId) params.brand_id = brandId
    }
    return params
  }, [user, isSuperAdmin, isBankAdmin, isLoanOfficer, activeTab])

  const { data: response, isFetching } = useFetchLeadScoresQuery(queryParams)
  const scores = response?.data ?? []

  return (
    <If
      condition={isFetching && !response}
      then={<ActivitySkeleton />}
      else={
        <div
          ref={containerRef}
          className="flex flex-col bg-white rounded-md py-4 md:pt-6 md:pb-4 w-full justify-between"
        >
          <div className="px-4 md:px-6">
            <h3 className="text-base font-medium mb-4">Scoreboard</h3>
            {/* ResizeObserver switches between tabs (≥512px) and dropdown (<512px) */}
            {showTabs ? (
              <PillTabs
                tabs={CATEGORY_TABS}
                currentTab={activeTab}
                onChange={id => setActiveTab(id as LeadScoreCategory)}
                className=""
                navigationClassName="w-full"
                commontabClassName="flex-1 text-center whitespace-nowrap px-3 py-2 border text-sm cursor-pointer hover:text-neutral-600 hover:border-neutral-400 transition-colors"
                activeTabClassName="bg-violet-50 border-violet-500 text-violet-800"
                defaultTabClassName="border-zinc-200 text-neutral-600"
              />
            ) : (
              <Select
                data={CATEGORY_SELECT_OPTIONS}
                selected={activeTab}
                label="Category"
                showLabel={false}
                setSelected={(id: string) =>
                  setActiveTab(id as LeadScoreCategory)
                }
              />
            )}
          </div>
          <div className="flex-1 px-4 md:px-6 mt-1 max-h-[400px] overflow-y-auto">
            {scores.length > 0 ? (
              <div className="divide-y divide-slate-50">
                {scores.map(score => (
                  <LeadScorePreviewCard key={score.id} score={score} />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-neutral-400">
                No leads in this category right now
              </div>
            )}
          </div>
          <div className="flex justify-end border-t border-gray-200 pt-4 pr-4 md:pr-6 mt-2">
            <Link
              className="text-sm font-medium text-[#4a1a9d] hover:underline"
              to="/lead-scoring"
            >
              View full scoreboard
            </Link>
          </div>
        </div>
      }
    />
  )
}

export default DashboardLeadScoringPreview
